import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { ThemeEvents, CartAddEvent } from '@theme/events';

const CART_DRAWER_NOTICE_DURATION_MS = 10000;

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {number | undefined} */
  #noticeTimeout;

  /** @type {string | null} */
  #pendingNoticeMessage = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    // CartErrorEvent is only dispatched from product-form (add-to-cart). Line-item qty errors use cart-items__error.
    document.addEventListener(ThemeEvents.cartError, this.#handleCartError);
    this.addEventListener(DialogOpenEvent.eventName, this.#onDialogOpen);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(ThemeEvents.cartError, this.#handleCartError);
    this.removeEventListener(DialogOpenEvent.eventName, this.#onDialogOpen);
    clearTimeout(this.#noticeTimeout);
    this.#pendingNoticeMessage = null;
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  #onDialogOpen = () => {
    this.#updateStickyState();
    this.#displayNotice();
  };

  /**
   * Shows cart-wide add-to-cart errors (e.g. partial add due to inventory) in the drawer header.
   * @param {Event} event
   */
  #handleCartError = (event) => {
    const message = event.detail?.data?.message;
    if (!message) return;

    this.#pendingNoticeMessage = message;
    this.#displayNotice();

    clearTimeout(this.#noticeTimeout);
    this.#noticeTimeout = window.setTimeout(() => {
      this.#pendingNoticeMessage = null;
      const notice = this.querySelector('[data-cart-drawer-notice]');
      const noticeText = this.querySelector('[data-cart-drawer-notice-text]');
      if (notice instanceof HTMLElement) notice.classList.add('hidden');
      if (noticeText instanceof HTMLElement) noticeText.textContent = '';
    }, CART_DRAWER_NOTICE_DURATION_MS);
  };

  #displayNotice() {
    const message = this.#pendingNoticeMessage;
    if (!message) return;

    const notice = this.querySelector('[data-cart-drawer-notice]');
    const noticeText = this.querySelector('[data-cart-drawer-notice-text]');
    if (!(notice instanceof HTMLElement) || !(noticeText instanceof HTMLElement)) return;

    noticeText.textContent = message;
    notice.classList.remove('hidden');
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
